import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleLogin, handleLogout, handlePasswordReset } from 'src/auth/AuthService';
import { getIdToken } from 'firebase/auth';
import PasswordResetModal from 'src/components/login/PasswordResetModal';
import SignUpModal from 'src/components/login/SignUpModal';
import { auth } from 'src/config/firebase';
import { useUser } from 'src/auth/UserContext';
import UserService from 'src/services/UserService';
import 'src/styles/login-page.css';

const LoginPage: React.FC = () => {
  const { setFirebaseUser, setPlatformUser } = useUser();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const navigate = useNavigate();

  const handleLoginClick = async () => {
    setErrorMessage(''); // Reset any existing error messages

    try {
      const user = await handleLogin(email, password, setFirebaseUser);

      if (user) {
        try {
          // Retrieve Firebase ID token to send to the backend
          const idToken = await getIdToken(auth.currentUser!, true);

          // Set the token and expiry in localStorage
          localStorage.setItem('authToken', idToken);
          const platformUser = await UserService.getUser();
          setPlatformUser(platformUser);

          // Navigate to the main page
          navigate('/main');
        } catch (sessionError) {
          console.error('Error creating session cookie:', sessionError.message);
          setErrorMessage('Failed to create session. Please try again.');
        }
      }
    } catch (loginError: any) {
      console.error('Login error:', loginError.message);
      setErrorMessage(loginError.message); // Display login error message
    }
  };

  const handlePasswordResetClick = async (email: string) => {
    try {
      await handlePasswordReset(email);
      setShowResetPasswordModal(false);
      alert('비밀번호 재설정 이메일이 발송되었습니다.');
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  };

  useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenExpiryTime = localStorage.getItem('tokenExpiryTime');
      if (tokenExpiryTime) {
        const currentTime = Date.now();
        if (currentTime > parseInt(tokenExpiryTime)) {
          handleLogout(setFirebaseUser, setPlatformUser, navigate);
          alert('세션이 만료되었습니다. 다시 로그인 해주세요.');
        }
      }
    };

    checkTokenExpiration();
  }, [navigate, setFirebaseUser]);

  return (
    <div>
      <div className="login-container">
        <h1>로그인</h1>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
        {errorMessage && <div className="login-error-message">{errorMessage}</div>}
        <div className="login-button-container">
          <button className="login-button" onClick={handleLoginClick}>
            Login
          </button>
          <button
            className="logout-button"
            onClick={() => handleLogout(setFirebaseUser, setPlatformUser, navigate)}
          >
            Logout
          </button>
        </div>
        {showSignUpModal && <SignUpModal onClose={() => setShowSignUpModal(false)} />}
        {showResetPasswordModal && (
          <PasswordResetModal
            onSubmit={handlePasswordResetClick}
            onClose={() => setShowResetPasswordModal(false)}
          />
        )}
        <p className="login-text-info">
          탑머티리얼 플랫폼은 탑머티리얼 소속 사용자만 이용 가능합니다. <br />
          <br />
          사용자 등록을 원하시면
          <span onClick={() => setShowSignUpModal(true)}> 여기</span> 를 클릭해 등록하세요. <br />
          비밀번호에 문제가 있으시면
          <span onClick={() => setShowResetPasswordModal(true)}> 여기</span> 를 클릭해 비밀번호를
          재설정 하세요. <br />
          <br />
          기타 문의가 있으시면
          <a href="mailto:c.roh@twinsketch.com"> c.roh@twinsketch.com</a>으로 연락부탁드립니다.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
